import React, { useState } from "react";

import { performUpdateUserReloadCarrierMini } from "../../../graphql/carrier/carrierCRUD";
import {
  administeredCarrierJunctions,
  carrierYardName,
  isCarrierAdmin,
} from "../../../utils/carrierUserUtils";

import { Button, Confirm, useNotify, useRedirect } from "react-admin";

import { makeStyles } from "@material-ui/core/styles";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import classnames from "classnames";

import { DeleteIcon } from "../../../components/common/iconography";
import { logError } from "../../../utils/appUtils";

import { sendEmails } from "../../../managerApp/resources/carriers/carrierUtils";
import { emailDeleteHTML } from "./emailDeleteHTML";
import { emailUserRemovedHTML } from "./emailUserRemovedHTML";

const useStyles = makeStyles(
  (theme) => ({
    deleteButton: {
      color: theme.palette.error.main,
      "&:hover": {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
    },
  }),
  { name: "RaDeleteWithConfirmButton" },
);

/**
 * Remove a colleague from the carrier account.
 *
 * Scoped to the memberships the acting user shares — see
 * administeredCarrierJunctions. Any other access the person holds is somebody
 * else's to withdraw, so the dialog names exactly what will go rather than
 * promising to delete "the user".
 */
export const DeleteUserButton = ({ record, appUser, className, ...props }) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const junctions = administeredCarrierJunctions(
    record?.reloadYardCarriers?.items,
    appUser?.reloadYardCarriers?.items,
  );

  // Nothing of this user's access is the acting user's to withdraw. Their own
  // record is not deleted from here either — that is Delete My Account, which
  // takes every yard with it and signs them out. The primary (admin) account
  // is likewise nobody else's to delete — only they themselves can, via their
  // own Delete My Account.
  if (!junctions.length || record?.id === appUser?.id || isCarrierAdmin(record))
    return null;

  const yardNameFor = (junction) =>
    carrierYardName(junction.reloadYardId, appUser?.reloadYardCarriers?.items);

  const yardNames = junctions.map(yardNameFor).filter(Boolean).join(", ");

  const handleDialogOpen = () => setIsOpen(true);
  const handleDialogClose = () => setIsOpen(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      const responses = await Promise.allSettled(
        junctions.map((junction) =>
          // deleted=true marks this an explicit removal, so a later restore of
          // the carrier does not bring the user back with it.
          performUpdateUserReloadCarrierMini({
            ...junction,
            active: "false",
            deleted: true,
          }),
        ),
      );
      // Each yard is its own row and its own outcome. A row that failed is not
      // rolled into one thrown error: the rows that succeeded are already
      // retired, and pretending otherwise would skip telling the user what
      // actually happened — or emailing the colleague about the access they
      // really did lose.
      const succeeded = junctions.filter(
        (_, index) => responses[index].status === "fulfilled",
      );
      const failed = junctions.filter(
        (_, index) => responses[index].status === "rejected",
      );

      // send silently don't await — one per yard, since a user removed from one
      // of several still has somewhere to sign in and should be told which one
      // they lost. Only the yards actually removed: an email about a yard whose
      // update failed would announce a removal that never happened.
      if (succeeded.length) {
        sendEmails(
          succeeded.map((junction) => ({
            email: record.email,
            firstName: record.firstName,
            lastName: record.lastName,
            reloadYardName: yardNameFor(junction),
          })),
          emailUserRemovedHTML,
        );
      }

      if (failed.length) {
        responses
          .filter((item) => item.status === "rejected")
          .forEach((item) =>
            logError(
              "carrierApp:carriers:DeleteUserButton:handleDelete",
              item.reason,
              false,
            ),
          );
        // Not rolled back: re-granting access the actor meant to withdraw is
        // the worse failure. The updates are idempotent, so staying here and
        // trying again finishes the job.
        const failedNames =
          failed.map(yardNameFor).filter(Boolean).join(", ") || "some yards";
        notify(
          `Could not remove access to ${failedNames}. Please try again.`,
          "warning",
        );
        return;
      }

      // A removed driver keeps their enrollment: biometric consent is the
      // driver's own, not the employer's to withdraw for them, so removal no
      // longer purges their face template or license details. Deleting it is
      // the driver's own doing, at any gate kiosk ("Delete my driver ID") —
      // not the carrier's, and not a yard manager's either: a yard's delete
      // withdraws that yard's access and erases the record only when no other
      // transload and no check-in history has a claim on it.
      notify("User deleted successfully", "success");
      redirect("/carrierUsers");
    } catch (error) {
      logError(
        "carrierApp:carriers:DeleteUserButton:handleDelete",
        error,
        notify,
      );
    } finally {
      setLoading(false);
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button
        {...props}
        label="Delete User"
        disabled={loading}
        onClick={handleDialogOpen}
        className={classnames(
          "ra-delete-button",
          classes.deleteButton,
          className,
        )}
      >
        <DeleteIcon />
      </Button>
      <Confirm
        isOpen={isOpen}
        loading={loading}
        title={"Delete Confirmation"}
        content={
          <>
            Remove {record?.firstName} {record?.lastName} from {yardNames}?{" "}
            <br />
            They will lose access to those transloads. If these are the last
            ones they work with, their sign-in is deleted too and any future
            invitation starts them at registration.
            {record?.userType === "driver" && (
              <>
                <br />
                Their driver ID and enrollment are kept — deleting their face
                and license data is theirs to do at any gate kiosk.
              </>
            )}
          </>
        }
        onConfirm={handleDelete}
        onClose={handleDialogClose}
      />
    </>
  );
};

export const DeleteAccountButton = ({
  record,
  className,
  backPath,
  ...props
}) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDialogOpen = () => setIsOpen(true);
  const handleDialogClose = () => setIsOpen(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      const junctions = record?.reloadYardCarriers?.items ?? [];
      const responses = await Promise.allSettled(
        junctions.map((item) =>
          performUpdateUserReloadCarrierMini({
            ...item,
            active: "false",
            deleted: true,
          }),
        ),
      );
      // Per-yard outcomes, as in DeleteUserButton above: rows that were
      // retired stay retired, rows that were not are said so.
      const succeeded = junctions.filter(
        (_, index) => responses[index].status === "fulfilled",
      );
      const failed = junctions.filter(
        (_, index) => responses[index].status === "rejected",
      );

      // send silently don't await — only for the yards actually deleted.
      if (succeeded.length) {
        sendEmails(
          succeeded.map((item) => ({ ...item, firstName: record.firstName })),
          emailDeleteHTML,
        );
      }

      if (failed.length) {
        responses
          .filter((item) => item.status === "rejected")
          .forEach((item) =>
            logError(
              "carrierApp:carriers:DeleteAccountButton:handleDelete:response",
              item.reason,
              false,
            ),
          );
        // No logout on a partial delete: the account still has live yards, and
        // signing the user out now leaves them believing the account is gone
        // while part of it can still be signed into. Stay, so they can retry.
        notify(
          "Could not delete every transload from the account. Please try again.",
          "warning",
        );
        return;
      }

      notify("Account deleted successfully", "success");
      redirect("/logout");
    } catch (error) {
      logError(
        "carrierApp:carriers:DeleteAccountButton:handleDelete",
        error,
        notify,
      );
    } finally {
      setLoading(false);
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button
        {...props}
        label="Delete My Account"
        disabled={loading}
        onClick={handleDialogOpen}
        className={classnames(
          "ra-delete-button",
          classes.deleteButton,
          className,
        )}
      >
        <DeleteIcon />
      </Button>
      <Confirm
        isOpen={isOpen}
        loading={loading}
        title={"Delete Confirmation"}
        content={
          <>
            Are you sure you want to delete your account? <br />
            You will lose access to all transloads! <br />
            Your sign-in goes with it — password and passkeys included. The
            address is free to register again, so a transload that invites you
            back starts you at registration. <br />
            {/* No advice about keeping one of them: access follows the company,
                so giving up a single transload is that transload's own decision
                to make about you, not something this form can do. */}
          </>
        }
        onConfirm={handleDelete}
        onClose={handleDialogClose}
      />
    </>
  );
};
